import { FC, useEffect, useState } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { Box, Colors, Typography } from '@altermaind/web/ui';
import { SelectItem } from '../types';
import { WrappedDropdown } from '../WrappedDropdown';
import { WrappedInput } from '../WrappedInput';

type Mode = 'dropdown' | 'text';

type Props = {
  control: Control<FieldValues>;
  name: string;
  label: string;
  createLabel: string;
  selectLabel: string;
  items: SelectItem[];
  placeholder?: string;
  loading?: boolean;
  hasError?: boolean;
  backgroundColorHeader?: keyof Colors;
  onItemSelect?: (item: SelectItem) => void;
  onModeChange?: (mode: Mode) => void;
  forcedMode?: Mode;
  showToggle?: boolean;
};

export const SelectOrCreate: FC<Props> = ({
  control,
  name,
  label,
  createLabel,
  selectLabel,
  items,
  placeholder,
  loading,
  hasError,
  backgroundColorHeader,
  onItemSelect,
  onModeChange,
  forcedMode,
  showToggle = true,
}) => {
  const [mode, setMode] = useState<Mode>('dropdown');

  const handleToggle = () => {
    const newMode: Mode = mode === 'dropdown' ? 'text' : 'dropdown';
    setMode(newMode);
    onModeChange?.(newMode);
  };

  const isToggleVisible = showToggle && forcedMode === undefined;

  useEffect(() => {
    setMode(forcedMode ?? 'dropdown');
  }, [forcedMode]);

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={12} mt={-2}>
        <Typography variant='p2/light' fontWeight='400' mt='none' color='grey/light'>
          {label}
        </Typography>
        {isToggleVisible && (
          <Typography
            as='span'
            variant='p3/light'
            color='primary'
            style={{ cursor: 'pointer' }}
            onClick={handleToggle}
          >
            {mode === 'dropdown' ? createLabel : selectLabel}
          </Typography>
        )}
      </Box>
      {mode === 'dropdown' ? (
        <WrappedDropdown
          control={control}
          name={name}
          items={items}
          placeholder={placeholder}
          loading={loading}
          // Error is not handled in dropdown mode — this component never has a selection error.
          // If error handling is needed in the future, uncomment the line below and run verification tests.
          // error={hasError}
          backgroundColorHeader={backgroundColorHeader}
          onItemSelect={onItemSelect}
        />
      ) : (
        <WrappedInput
          control={control}
          name={name}
          placeholder={label}
          hasError={hasError || false}
        />
      )}
    </Box>
  );
};
